<script lang="ts">
	/**
	 * The reconciliation report (Design §16 step 8).
	 *
	 * Reconciliation is the store's own audit of itself against the payment provider, and two of
	 * its counts are the two directions of one signal:
	 *
	 * - **`driftCorrected`** — rows the pass found wrong and put right. One is routine. A number
	 *   that climbs run after run means something upstream is producing drift faster than the
	 *   nightly pass can absorb it, which is why the history is shown as a series and the trend is
	 *   spelled out. A single number cannot rise.
	 * - **`providerActiveWithoutLocalRow`** — a subscription the provider is *still charging a card
	 *   for* that this store holds no live row for. It is the costliest drift in the system: the
	 *   customer keeps paying and has nothing, and no other mechanism in the store can see it —
	 *   there is no local row to notice missing. Anything above zero is a billing incident.
	 */

	import { onMount } from 'svelte';
	import { Badge, Button, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import { EmptyState, Panel, RelativeDate } from '../../../components';
	import { getReconciliation, runReconciliation } from '../../../AppNetwork';
	import type { ReconciliationHistory, ReconciliationRun } from '../../../AppTypes';
	import { toastError, toastSuccess } from '../../../stores/toast';

	let history: ReconciliationHistory | null = null;
	let loading: boolean = true;
	let loadError: string = '';
	let running: boolean = false;

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			history = await getReconciliation();
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	async function fnRun(): Promise<void> {
		running = true;
		try {
			await runReconciliation();
			toastSuccess('The reconciliation pass finished.');
			await fnLoad();
		} catch (error) {
			toastError('The reconciliation pass did not run.', (error as Error).message);
		} finally {
			running = false;
		}
	}

	$: latest = history?.latest ?? null;
	$: report = latest?.report ?? null;

	/** Newest first, and only runs that produced a report — a failed run has no counts to trend. */
	$: reported = [...(history?.items ?? [])]
		.filter((run): run is ReconciliationRun & { report: NonNullable<ReconciliationRun['report']> } => run.report !== null)
		.sort((left, right) => new Date(right.startedAt).getTime() - new Date(left.startedAt).getTime());

	/**
	 * Drift read as a series rather than as one number: two runs is enough to say "rising", and
	 * rising is the reading that matters — the absolute count is noise without it.
	 */
	$: driftTrend = (() => {
		if (reported.length < 2) return null;
		const current = reported[0].report.driftCorrected;
		const previous = reported[1].report.driftCorrected;
		if (current > previous) return { direction: 'rising', current, previous };
		if (current < previous) return { direction: 'falling', current, previous };
		return { direction: 'steady', current, previous };
	})();

	/**
	 * Every entry carries `note`, even where it is empty.
	 *
	 * A field present on one entry and absent from the rest makes the array's inferred type a union,
	 * and the loop below then renders whichever member it was handed — which is how a note added to
	 * one figure ends up silently dropped for every other. Uniform is cheaper than remembering.
	 */
	$: counts = report === null ? [] : ([
		{ label: 'Provider subscriptions walked', value: report.providerSubscriptionsWalked },
		{ label: 'Entitlements recomputed', value: report.entitlementsRecomputed },
		{ label: 'Sessions rescued', value: report.sessionsRescued },
		{ label: 'Sessions expired', value: report.sessionsExpired },
		{ label: 'Events requeued', value: report.eventsRequeued },
		{ label: 'Dead letters found', value: report.deadDeliveries },
		{ label: 'Resyncs enqueued', value: report.resyncsEnqueued },
		{ label: 'Refunds retried', value: report.refundsRetried },
		{ label: 'Stale changes failed', value: report.staleChangesFailed },
		{ label: 'Family duplicates found', value: report.familyDuplicatesFound },
		{ label: 'Family duplicates cancelled', value: report.familyDuplicatesCancelled },
		{ label: 'Tax rates checked', value: report.taxRatesExamined },
		// The one to read: a non-zero here is subscriptions that were billing the wrong tax until
		// last night, which nothing else in the system reports.
		{ label: 'Tax rates corrected', value: report.taxRatesUpdated },
		{ label: 'Orphaned references', value: report.orphanedRefs },
		{ label: 'Event payloads pruned', value: report.prunedEventPayloads },
		{ label: 'Delivery payloads pruned', value: report.prunedDeliveryPayloads },
		{ label: 'Entitlement changes pruned', value: report.prunedEntitlementChanges },
		/*
		 * §12.8. Analytics retention runs as a step of this pass rather than as a worker of its own,
		 * so this report is the **only** evidence anywhere that it happened — there is no heartbeat
		 * to look for and nothing on the storefront changes when it stops. A step whose output is
		 * not surfaced is a step nobody can tell has stopped running, which is the failure the
		 * heartbeats and this whole panel exist to prevent.
		 */
		{ label: 'Analytics rows anonymised', value: report.analyticsAnonymised },
		{ label: 'Analytics rows archived', value: report.analyticsArchived },
		{
			label: 'Analytics events deleted',
			value: report.analyticsDeleted,
			// A capped delete is work left behind. Silently stopping at the bound and reporting a
			// number is indistinguishable from having finished.
			note: report.analyticsDeleteCapped ? 'hit the per-pass bound; the next pass continues' : ''
		}
	] as { label: string; value: number; note?: string }[]);

	const statusColors: Record<string, 'green' | 'blue' | 'red'> = {
		running: 'blue',
		completed: 'green',
		failed: 'red'
	};

	onMount(() => fnLoad());
</script>

<Panel title="Reconciliation" faIcon="scale-balanced">
	<svelte:fragment slot="titleRight">
		<Button size="xs" color="primary" disabled={running} on:click={fnRun}>
			<i class="fa-solid fa-play mr-2" />{running ? 'Running…' : 'Run now'}
		</Button>
	</svelte:fragment>

	<div class="space-y-4">
		<p class="text-default text-sm">
			The store's audit of itself against the payment provider. It runs nightly; running it now
			is how a drift you have just fixed is watched falling rather than waited out.
		</p>

		{#if loading}
			<EmptyState variant="loading" title="Loading the reconciliation report…" />
		{:else if loadError.length > 0}
			<EmptyState variant="error" title="The reconciliation report could not be loaded." description={loadError}>
				<Button color="alternative" size="sm" on:click={() => fnLoad()}>Try again</Button>
			</EmptyState>
		{:else if latest === null || report === null}
			<EmptyState
				title="Reconciliation has not produced a report yet."
				description="Until it runs, nothing is comparing this store against the payment provider."
			>
				<Button color="primary" size="sm" disabled={running} on:click={fnRun}>Run now</Button>
			</EmptyState>
		{:else}
			<div class="flex flex-wrap items-center gap-3 text-sm">
				<Badge color={statusColors[latest.status] ?? 'dark'}>{latest.status}</Badge>
				<span class="text-default">{latest.kind === 'nightly' ? 'Nightly pass' : 'Minutes pass'}</span>
				<span class="text-default">started <RelativeDate date={latest.startedAt} /></span>
				{#if latest.durationMs !== null}
					<span class="text-default">in {(latest.durationMs / 1000).toFixed(1)}s</span>
				{/if}
				{#if latest.error}
					<span class="text-red-600 dark:text-red-400">{latest.error}</span>
				{/if}
			</div>

			<!-- The two counts the rest of the panel exists to frame. -->
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
				<div class="widget bg-depth border-widget {report.driftCorrected > 0 ? 'border-amber-400 dark:border-amber-500' : ''}">
					<div class="card-label"><i class="fa-solid fa-wrench text-[11px] mr-1" />Drift corrected</div>
					<div class="text-3xl font-semibold mt-1">{report.driftCorrected}</div>
					<p class="text-sm mt-2">
						Subscriptions or entitlements this store had wrong and the pass put right against the
						provider. One is routine; a count that keeps climbing means drift is being created
						faster than it is being absorbed.
					</p>
					{#if driftTrend !== null}
						<p class="text-sm mt-2 font-medium">
							{#if driftTrend.direction === 'rising'}
								Drift is <strong>rising</strong>: {driftTrend.current} corrected in this run, up from
								{driftTrend.previous} in the one before.
							{:else if driftTrend.direction === 'falling'}
								Drift is falling: {driftTrend.current} corrected in this run, down from
								{driftTrend.previous} in the one before.
							{:else}
								Drift is steady at {driftTrend.current} corrected, the same as the run before.
							{/if}
						</p>
					{/if}
				</div>

				<div class="widget bg-depth border-widget {report.providerActiveWithoutLocalRow > 0 ? 'border-red-500 dark:border-red-500' : ''}">
					<div class="card-label"><i class="fa-solid fa-circle-exclamation text-[11px] mr-1" />Active at the provider, missing here</div>
					<div class="text-3xl font-semibold mt-1 {report.providerActiveWithoutLocalRow > 0 ? 'text-red-600 dark:text-red-400' : ''}">
						{report.providerActiveWithoutLocalRow}
					</div>
					{#if report.providerActiveWithoutLocalRow > 0}
						<p class="text-sm mt-2">
							<strong>The payment provider is still charging a card for
							{report.providerActiveWithoutLocalRow}
							{report.providerActiveWithoutLocalRow === 1 ? 'subscription' : 'subscriptions'} this store
							holds no live row for.</strong>
							Those customers are paying and have nothing. Nothing else in the store can detect this, because
							there is no local row to notice missing, so this count is the only warning there is.
							Treat it as a billing incident: find the subscriptions at the provider and either
							restore the local rows or cancel the charges.
						</p>
					{:else}
						<p class="text-sm mt-2">
							No subscription is being charged at the provider without a live row here. Above zero,
							this means customers are paying for nothing, and no other mechanism can see it.
						</p>
					{/if}
				</div>
			</div>

			<div>
				<div class="card-label mb-2"><i class="fa-solid fa-triangle-exclamation text-[11px] mr-1" />Anomalies</div>
				{#if report.anomalies.length === 0}
					<p class="text-default text-sm">The pass reported no anomalies.</p>
				{:else}
					<ul class="list-disc pl-5 space-y-1 text-sm">
						{#each report.anomalies as anomaly, index (`${index}-${anomaly}`)}
							<li>{anomaly}</li>
						{/each}
					</ul>
				{/if}
			</div>

			<div>
				<div class="card-label mb-2">What the pass touched</div>
				<div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
					{#each counts as count (count.label)}
						<div class="widget bg-depth">
							<div class="card-label">{count.label}</div>
							<div class="text-xl font-semibold mt-1">{count.value}</div>
							{#if (count.note ?? '') !== ''}
								<div class="text-xs text-amber-600 dark:text-amber-400 mt-1">{count.note}</div>
							{/if}
						</div>
					{/each}
				</div>
			</div>

			<div>
				<div class="card-label mb-2">Oldest still waiting when the pass ran</div>
				<div class="grid grid-cols-1 md:grid-cols-3 gap-3">
					<div class="widget bg-depth">
						<div class="card-label">Unprocessed provider event</div>
						<div class="mt-1">
							{#if report.oldestUnprocessedEventAt}
								<RelativeDate date={report.oldestUnprocessedEventAt} />
							{:else}
								<span class="text-default">Nothing waiting</span>
							{/if}
						</div>
					</div>
					<div class="widget bg-depth">
						<div class="card-label">Pending delivery</div>
						<div class="mt-1">
							{#if report.oldestPendingDeliveryAt}
								<RelativeDate date={report.oldestPendingDeliveryAt} />
							{:else}
								<span class="text-default">Nothing waiting</span>
							{/if}
						</div>
					</div>
					<div class="widget bg-depth">
						<div class="card-label">Pending email</div>
						<div class="mt-1">
							{#if report.oldestPendingEmailAt}
								<RelativeDate date={report.oldestPendingEmailAt} />
							{:else}
								<span class="text-default">Nothing waiting</span>
							{/if}
						</div>
					</div>
				</div>
			</div>

			<div>
				<div class="card-label mb-2">Recent runs</div>
				{#if (history?.items ?? []).length === 0}
					<EmptyState compact title="No run history." />
				{:else}
					<div class="relative overflow-x-auto">
						<Table>
							<TableHead>
								<TableHeadCell>Started</TableHeadCell>
								<TableHeadCell>Kind</TableHeadCell>
								<TableHeadCell>Status</TableHeadCell>
								<TableHeadCell>Drift corrected</TableHeadCell>
								<TableHeadCell>Active at provider, missing here</TableHeadCell>
								<TableHeadCell>Anomalies</TableHeadCell>
								<TableHeadCell>Duration</TableHeadCell>
							</TableHead>
							<TableBody>
								{#each history?.items ?? [] as run (run.uuid)}
									<TableBodyRow>
										<TableBodyCell class="dataTable"><RelativeDate date={run.startedAt} /></TableBodyCell>
										<TableBodyCell class="dataTable">{run.kind}</TableBodyCell>
										<TableBodyCell class="dataTable">
											<Badge color={statusColors[run.status] ?? 'dark'}>{run.status}</Badge>
										</TableBodyCell>
										<TableBodyCell class="dataTable">{run.report?.driftCorrected ?? '—'}</TableBodyCell>
										<TableBodyCell class="dataTable">{run.report?.providerActiveWithoutLocalRow ?? '—'}</TableBodyCell>
										<TableBodyCell class="dataTable">{run.report?.anomalies.length ?? '—'}</TableBodyCell>
										<TableBodyCell class="dataTable">
											{run.durationMs === null ? '—' : `${(run.durationMs / 1000).toFixed(1)}s`}
										</TableBodyCell>
									</TableBodyRow>
								{/each}
							</TableBody>
						</Table>
					</div>
				{/if}
			</div>
		{/if}
	</div>
</Panel>
